import React, { useCallback, useState } from "react";
import "./MarketingCTA.css";

const API_BASE = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(
  /\/+$/,
  ""
);

const AUTH_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const initialNewsletterForm = {
  fullName: "",
  email: "",
  phone: "",
  businessName: "",
  interestType: "general",
};

const initialContactForm = {
  fullName: "",
  email: "",
  phone: "",
  businessName: "",
  interestType: "general",
  message: "",
};

const getToken = () => {
  for (const key of AUTH_KEYS) {
    const value = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (value && String(value).trim()) return String(value).trim();
  }

  return "";
};

const clearAuthStorage = () => {
  AUTH_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  window.dispatchEvent(new Event("auth-state-changed"));
};

const buildHeaders = () => {
  const token = getToken();

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const isValidEmail = (email = "") => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const normalizePayload = (form = {}) => ({
  fullName: form.fullName.trim(),
  email: form.email.trim().toLowerCase(),
  phone: form.phone.trim(),
  businessName: form.businessName.trim(),
  interestType: form.interestType,
  ...(form.message !== undefined ? { message: form.message.trim() } : {}),
  sourcePage: "marketing",
});

function MarketingCTA() {
  const [newsletterForm, setNewsletterForm] = useState(initialNewsletterForm);
  const [contactForm, setContactForm] = useState(initialContactForm);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState({ type: "", message: "" });
  const [contactStatus, setContactStatus] = useState({ type: "", message: "" });

  const handleNewsletterChange = (event) => {
    const { name, value } = event.target;

    setNewsletterForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContactChange = (event) => {
    const { name, value } = event.target;

    setContactForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateNewsletter = () => {
    if (!newsletterForm.fullName.trim()) return "Full name is required.";

    if (!isValidEmail(newsletterForm.email.trim())) {
      return "Please enter a valid email address.";
    }

    return "";
  };

  const validateContact = () => {
    if (!contactForm.fullName.trim()) return "Full name is required.";

    if (!isValidEmail(contactForm.email.trim())) {
      return "Please enter a valid email address.";
    }

    if (!contactForm.message.trim()) return "Message is required.";

    return "";
  };

  const submitMarketingForm = useCallback(async (endpoint, payload) => {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 401) {
      clearAuthStorage();
      throw new Error("Session expired. Please login again.");
    }

    if (!response.ok || !data?.success) {
      throw new Error(data?.message || "Request failed.");
    }

    return data;
  }, []);

  const handleNewsletterSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateNewsletter();

    setNewsletterStatus({ type: "", message: "" });

    if (validationError) {
      setNewsletterStatus({ type: "error", message: validationError });
      return;
    }

    try {
      setNewsletterLoading(true);

      const data = await submitMarketingForm(
        "/api/marketing/subscribe",
        normalizePayload(newsletterForm)
      );

      setNewsletterStatus({
        type: "success",
        message: data.message || "Subscribed successfully.",
      });

      setNewsletterForm(initialNewsletterForm);
      window.dispatchEvent(new Event("marketing-lead-created"));
    } catch (error) {
      setNewsletterStatus({
        type: "error",
        message: error.message || "Something went wrong.",
      });
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateContact();

    setContactStatus({ type: "", message: "" });

    if (validationError) {
      setContactStatus({ type: "error", message: validationError });
      return;
    }

    try {
      setContactLoading(true);

      const data = await submitMarketingForm(
        "/api/marketing/contact-sales",
        normalizePayload(contactForm)
      );

      setContactStatus({
        type: "success",
        message: data.message || "Sales enquiry submitted successfully.",
      });

      setContactForm(initialContactForm);
      window.dispatchEvent(new Event("marketing-lead-created"));
    } catch (error) {
      setContactStatus({
        type: "error",
        message: error.message || "Something went wrong.",
      });
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <section className="marketing-cta-section">
      <div className="marketing-container">
        <div className="marketing-cta-box">
          <span className="marketing-section-badge">Get Started</span>

          <h2>Ready to Grow Your Business Online?</h2>

          <p>
            Subscribe to the newsletter or send your sales enquiry. Your data is
            cleaned, validated, and safely stored without duplication issues.
          </p>
        </div>

        <div className="marketing-form-grid">
          <div className="marketing-form-card" id="marketing-newsletter-form">
            <h3>Newsletter Subscription</h3>
            <p>Subscribe to updates, offers, and business marketing tips.</p>

            <form onSubmit={handleNewsletterSubmit} className="marketing-form">
              <div className="marketing-form-group">
                <label htmlFor="newsletter-full-name">Full Name</label>
                <input
                  id="newsletter-full-name"
                  type="text"
                  name="fullName"
                  value={newsletterForm.fullName}
                  onChange={handleNewsletterChange}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="marketing-form-group">
                <label htmlFor="newsletter-email">Email</label>
                <input
                  id="newsletter-email"
                  type="email"
                  name="email"
                  value={newsletterForm.email}
                  onChange={handleNewsletterChange}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="marketing-form-group">
                <label htmlFor="newsletter-phone">Phone</label>
                <input
                  id="newsletter-phone"
                  type="text"
                  name="phone"
                  value={newsletterForm.phone}
                  onChange={handleNewsletterChange}
                  autoComplete="tel"
                />
              </div>

              <div className="marketing-form-group">
                <label htmlFor="newsletter-business">Business</label>
                <input
                  id="newsletter-business"
                  type="text"
                  name="businessName"
                  value={newsletterForm.businessName}
                  onChange={handleNewsletterChange}
                  autoComplete="organization"
                />
              </div>

              <div className="marketing-form-group">
                <label htmlFor="newsletter-interest">Interest</label>
                <select
                  id="newsletter-interest"
                  name="interestType"
                  value={newsletterForm.interestType}
                  onChange={handleNewsletterChange}
                >
                  <option value="general">General</option>
                  <option value="seo">SEO</option>
                  <option value="email_campaigns">Email Campaigns</option>
                  <option value="brand_promotion">Brand Promotion</option>
                </select>
              </div>

              {newsletterStatus.message ? (
                <output className={`marketing-form-alert ${newsletterStatus.type}`}>
                  {newsletterStatus.message}
                </output>
              ) : null}

              <button
                type="submit"
                className="marketing-primary-btn marketing-submit-btn"
                disabled={newsletterLoading}
              >
                {newsletterLoading ? "Submitting..." : "Subscribe"}
              </button>
            </form>
          </div>

          <div className="marketing-form-card" id="marketing-contact-form">
            <h3>Contact Sales</h3>
            <p>Tell us about your needs and our team will connect with you.</p>

            <form onSubmit={handleContactSubmit} className="marketing-form">
              <div className="marketing-form-group">
                <label htmlFor="contact-full-name">Full Name</label>
                <input
                  id="contact-full-name"
                  type="text"
                  name="fullName"
                  value={contactForm.fullName}
                  onChange={handleContactChange}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="marketing-form-group">
                <label htmlFor="contact-email">Email</label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={contactForm.email}
                  onChange={handleContactChange}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="marketing-form-group">
                <label htmlFor="contact-phone">Phone</label>
                <input
                  id="contact-phone"
                  type="text"
                  name="phone"
                  value={contactForm.phone}
                  onChange={handleContactChange}
                  autoComplete="tel"
                />
              </div>

              <div className="marketing-form-group">
                <label htmlFor="contact-business">Business</label>
                <input
                  id="contact-business"
                  type="text"
                  name="businessName"
                  value={contactForm.businessName}
                  onChange={handleContactChange}
                  autoComplete="organization"
                />
              </div>

              <div className="marketing-form-group">
                <label htmlFor="contact-interest">Interest</label>
                <select
                  id="contact-interest"
                  name="interestType"
                  value={contactForm.interestType}
                  onChange={handleContactChange}
                >
                  <option value="general">General</option>
                  <option value="seo">SEO</option>
                  <option value="email_campaigns">Email Campaigns</option>
                  <option value="brand_promotion">Brand Promotion</option>
                </select>
              </div>

              <div className="marketing-form-group">
                <label htmlFor="contact-message">Message</label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={contactForm.message}
                  onChange={handleContactChange}
                  rows={4}
                  required
                />
              </div>

              {contactStatus.message ? (
                <output className={`marketing-form-alert ${contactStatus.type}`}>
                  {contactStatus.message}
                </output>
              ) : null}

              <button
                type="submit"
                className="marketing-primary-btn marketing-submit-btn"
                disabled={contactLoading}
              >
                {contactLoading ? "Submitting..." : "Send Enquiry"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default MarketingCTA;